/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef, useMemo } from 'react';

import PropTypes from 'prop-types';
import { Button, Spinner, Tabs, Typography } from '@telus-uds/components-web';
import axios from 'axios';
import { Image, Error } from '../design/elements';
import RichText from '../richText';
import FormStepWLS from './formStepWLS';
import FormStepBRP from './formStepBRP';
import FormStepFM from './formStepFM';
import FormStepOCDR from './formStepOCDR';
import FormStepVulcan from './formStepVulcan';
import { mergeOrderDataAfterSubmit } from './vulcanFormsCoreAdapters/mergeOrderDataAfterSubmit';
import { FormWrapper, TabBarWrapper } from './css';
import HardwareGridUpload from '../HardwareGridUpload';
import {
  wlsSubmitPayload,
  brpSubmitPayload,
  saveAndExit,
  fmSubmitPayload,
  ocdrSubmitPayload,
  vulcanSubmitPayload,
  fmSOWSubmitPayload,
  csSubmitPayload
} from './formSubmitLogic';
import MyOrdersDashboardFM from '../dashboards/myOrdersDashboardFM';
import MyOrdersDashboardCS from '../dashboards/myOrdersDashboardCS';
import MyOrdersDashboardOCDR from '../dashboards/myOrdersDashboardOCDR';
import OrderFulfillmentDashboardOCDR from '../dashboards/orderFulfillmentDashboardOCDR';
import MyOrdersDashboardVUL from '../dashboards/myOrdersDashboardVUL';
import MyOrdersDashboardFMSOW from '../dashboards/myOrdersDashboardFMSOW';
import SalesRepDashboard from '../dashboards/salesRepDashboard';
import OrderFulfillmentDashboardFM from '../dashboards/orderFulfilmentDashboardFM';
import OrderFulfillmentDashboardCS from '../dashboards/orderFulfillmentDashboardCS';
import OrderFulfillmentDashboardVUL from '../dashboards/orderFulfillmentDashboardVUL';
import OrderFulfillmentDashboardFMSOW from '../dashboards/orderFulfilmentDashboardFMSOW';
import DiscountApprovalDashboardFM from '../dashboards/discountApprovalDashboardFM';
import AllOrdersDashboardWLS from '../dashboards/allOrdersDashboardWLS';
import ChannelOrdersDashboardWLS from '../channelOrdersDashboardWLS';
import BrpConfirmationBox from './BRP/BrpConfirmationBox';

import {
  wirelessLandingPageForm,
  wirelessOrderForm,
  fieldProductSelection,
  valueCustomerInformation,
  valueProductInformation,
  BACKEND_KEYS,
  categoryWireless,
  DevicesOnlyEnglish,
  DevicesOnlyFrench,
  AccessoriesOnlyEnglish,
  AccessoriesOnlyFrench,
  categoryBrp,
  brpPrequalification,
  brpOrderForm,
  sevenX,
  categoryFullyManaged,
  fullyManagedForm,
  fullyManagedLandingPage,
  categoryVulcan,
  vulcanForm,
  vulcanLandingPage,
  categoryFullyManagedSOW,
  fullyManagedSOWForm,
  fullyManagedSOWLandingPage,
  emailForSOW,
  categoryCybersecurity,
  cybersecurityForm,
  cybersecurityLandingPage,
  categoryOCDR,
  ocdrForm,
  ocdrLandingPage,
  fieldMicrosoftCloudAndAdvisoryServices
} from '../utils/constant';
import EN from '../../EN.json';
import FR from '../../FR.json';
import request, { apiHost, config } from '../utils/request';
import { requestOrderPdf, openPdfPlaceholderWindow } from '../utils/orderPdf';
import { getPathVariables } from '../misc';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { useLocaleContext } from '../context/LocaleContext';
import FormStepFMSOW from './formStepFMSOW';
import FormStepCS from './formStepCS';

const Form = ({
  data,
  orderId = '',
  history,
  initialStep = '',
  isEditing = '',
  dataSummary
}) => {
  if (!data) return <Error text="Error!" />;

  const {
    fields: {
      image,
      description,
      steps,
      skipIntro,
      hideProgressBar,
      showOrdersList
    }
  } = data;
  const local = useLocaleContext();

  const localizedStrings = useLocalizedStringsContext();
  const {
    WLS: {
      TABS: { MyOrders, AllOrders, ChannelOrders }
    }
  } = localizedStrings;
  const {
    formButtons: {
      newQuoteButtonLabel,
      ManageDevicesCreditsFee,
      newOrderButtonLabel,
      TELUSWirelessSalesOrderForm
    },
    vulcanWebform: { vulBusinessConnect },
    dashboardVUL: { myOrderHeader, orderFulfilmentHeader }
  } = local === 'en-US' ? EN : FR;
  const [step, setStep] = useState(
    (initialStep && parseInt(initialStep, 10)) || (skipIntro ? 1 : 0)
  );
  const [stepData, setStepData] = useState();
  const [progressBarSteps, setProgressBarSteps] = useState([]);
  const [orderPermissions, setOrderPermissions] = useState([]);
  const [orderData, setOrderData] = useState();
  const [showLoading, setShowLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isCreateOrderButtonVisible, setIsCreateOrderButtonVisible] = useState(
    false
  );
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const formRef = useRef(null);
  const setFormRef = node => {
    formRef.current = node;
  };
  const stableOrderData = useMemo(() => orderData || {}, [orderData]);

  const {
    location: { pathname, search }
  } = history;
  const [locale, categoryName, formName] = getPathVariables(pathname);

  const userProfile = JSON.parse(localStorage.getItem('userProfile'));
  const {
    role: { name: roleName = '' } = {},
    channel,
    email,
    canSubmitOrders
  } = userProfile || {};
  const isChannelSevenX = channel && channel.name === sevenX;

  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    if (roleName === 'ORDER_FULFILLMENT') {
      return 'OrderFulfillmentDashboard';
    }
    return savedTab || 'MyOrders';
  });

  useEffect(() => {
    const savedTab = localStorage.getItem('activeTab');
    if (savedTab) {
      localStorage.removeItem('activeTab');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const discount = params.get('discount');
    if (discount) {
      setSelectedDiscount(discount);
      setActiveTab('DiscountApprovalDashboard');
    } else {
      setSelectedDiscount(null);
    }
  }, []);

  useEffect(() => {
    if (showOrdersList) {
      setOrderData(undefined);
    }
  }, [showOrdersList]);

  // Keyed on the route, not the Contentful flag: the Vulcan landing page must
  // never hold a previous order (browser Back / in-app nav from Vulcan_Form).
  useEffect(() => {
    if (formName === vulcanLandingPage) {
      setOrderData(undefined);
    }
  }, [formName]);

  const getLocaleLocalValues = () => {
    if (locale === 'fr-CA') return FR;
    return EN;
  };

  const getToolPath = () => {
    if (categoryName === categoryWireless)
      return `/${locale}/wireless/Wireless_Landing_Page?page=1`;
    if (categoryName === categoryFullyManaged) {
      return `/${locale}/fully_managed/Fully_Managed_Landing_Page?page=1`;
    }
    if (categoryName === categoryVulcan) {
      return `/${locale}/vulcan/Vulcan_Landing_Page?page=1`;
    }
    if (categoryName === categoryFullyManagedSOW) {
      return `/${locale}/fully_managed_SOW/Fully_Managed_SOW_Landing_Page?page=1`;
    }
    if (categoryName === categoryOCDR) {
      return `/${locale}/ocdr/OCDR_Landing_Page?page=1`;
    }
    if (categoryName === categoryCybersecurity) {
      return `/${locale}/cybersecurity/Cybersecurity_Landing_Page?page=1`;
    }
    return '/';
  };

  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        StepNameVariations: {
          WirelessOrderDetails,
          DeviceOrderDetails,
          AccessoryOrderDetails
        }
      }
    }
  } = getLocaleLocalValues();

  const updateStep = newStep => {
    setStep(newStep);
  };

  const updateProgressBarSteps = productSelection => {
    let result = steps.filter(item =>
      [valueCustomerInformation, valueProductInformation].includes(
        item.fields.backendKey
      )
    );
    if (productSelection && productSelection.length) {
      result = [
        ...result,
        ...productSelection.map(item => ({
          fields: { name: item }
        }))
      ];
    }
    const quoteSummaryStep = steps.find(
      stepForm => stepForm.fields.backendKey === BACKEND_KEYS.quoteSummary
    );
    if (quoteSummaryStep) {
      result = [...result, quoteSummaryStep];
    }
    setProgressBarSteps(result);
  };

  const fetchOrderPermissions = async () => {
    try {
      const result = await request.get('/order-permissions-dates');
      if (result && result.status === 200) {
        setOrderPermissions(result.data.data);
      }
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchOrderPermissions();
  }, []);

  useEffect(() => {
    // Drop a response that lands after the user has left this order (e.g.
    // browser Back to a landing page) so it can't re-populate stale state.
    let isStale = false;
    if (orderId) {
      if (categoryName === categoryWireless) {
        axios.get(`${apiHost}/orders/${orderId}`, config()).then(response => {
          if (!isStale && response && response.data) {
            setOrderData(response.data);
            updateProgressBarSteps(response.data[fieldProductSelection]);
          }
        });
      } else {
        const api = categoryName === categoryBrp ? 'brp' : 'orders';
        request.get(`/${api}/${orderId}`).then(response => {
          if (!isStale && response && response.data) {
            setOrderData(response.data);
            updateProgressBarSteps(response.data[fieldProductSelection]);
          }
        });
      }
    } else {
      updateProgressBarSteps();
    }
    return () => {
      isStale = true;
    };
  }, [orderId]);
  /**
   * Whenever locale changes, following code updates the stepData and progressBarSteps
   * acccordingly
   */
  useEffect(() => {
    let result;
    if (
      categoryName === categoryWireless ||
      categoryName === categoryFullyManaged ||
      categoryName === categoryVulcan ||
      categoryName === categoryFullyManagedSOW ||
      categoryName === categoryCybersecurity ||
      categoryName === categoryOCDR
    ) {
      setStepData(steps[step - 1]);
    } else {
      if (progressBarSteps && progressBarSteps.length > 0) {
        if (orderData && orderData.productSelection) {
          updateProgressBarSteps(orderData.productSelection);
        }
        if (progressBarSteps[step - 1]) {
          const stepName = progressBarSteps[step - 1].fields.name;
          result = steps.find(item => item.fields.name === stepName);
        }
      } else {
        result = steps[step - 1];
      }
      setStepData(result);
    }
  }, [data, step, orderData]);

  const updateStepData = () => {
    let result;
    if (progressBarSteps[step - 1]) {
      const stepName = progressBarSteps[step - 1].fields.name;
      result = steps.find(item => item.fields.name === stepName);
    } else {
      result = steps[step - 1];
    }
    setStepData(result);
  };

  useEffect(() => {
    if (progressBarSteps && formName !== wirelessLandingPageForm) {
      updateStepData();
    }
  }, [progressBarSteps]);

  useEffect(() => {
    if (formName !== wirelessLandingPageForm) {
      updateStepData();
    }
  }, [step]);

  const showQuoteButton = () => {
    if (roleName === 'ORDER_FULFILLMENT') {
      return false;
    }
    const smOrderGatedForms = [
      categoryWireless,
      categoryBrp,
      categoryVulcan,
      categoryFullyManaged,
      categoryOCDR,
      categoryCybersecurity
    ];
    if (
      roleName === 'SALES_MANAGER' &&
      smOrderGatedForms.includes(categoryName) &&
      !canSubmitOrders
    ) {
      return false;
    }
    if (
      roleName !== 'ADMIN' &&
      roleName !== 'SALES_REP' &&
      !(roleName === 'SALES_MANAGER' && canSubmitOrders) &&
      categoryName === categoryVulcan
    ) {
      return false;
    }
    return true;
  };

  const fileAlreadyExists = (orderPayload, newKey) =>
    orderPayload.files.some(item => Object.keys(item) === newKey);

  const attachedFiles = (orderPayload, formData) => {
    if ([1, 5].includes(step)) {
      if (orderPayload && orderPayload.files) {
        if (formData.files && formData.files && formData.files.length) {
          formData.files.forEach(item => {
            if (!fileAlreadyExists(orderPayload, Object.keys(item))) {
              orderPayload.files.push(item);
            }
          });
        }
      }
    }
    return orderPayload;
  };

  const onWlsFormSubmit = async (
    formData,
    isFormLastStep,
    shouldUpdateStep = true
  ) => {
    let newStep;
    if (formName === wirelessLandingPageForm) {
      newStep = isChannelSevenX ? 2 : 1;
    } else {
      newStep = step + 1;
    }
    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    let response = {};
    // SOF-1545
    if (orderData && orderData.smbIndirectChannelSalesRep) {
      const updatedChannelName = orderData.smbIndirectChannelSalesRep;
      orderData.loggedInUserDetail.channelName = updatedChannelName;
    }
    if (step === 1 || step === 4) {
      // Send order data to API
      response = await wlsSubmitPayload(
        {
          ...orderData,
          ...formData,
          locale,
          categoryName,
          formName,
          step: newStep
        },
        isFormLastStep,
        orderPermissions
      );
    } else {
      // Send order data to API
      const tempOrderData = attachedFiles(orderData, formData);
      response = await wlsSubmitPayload(
        {
          ...tempOrderData,
          ...formData,
          locale,
          categoryName,
          formName,
          step: newStep
        },
        isFormLastStep,
        orderPermissions
      );
    }
    setOrderData({ ...orderData, ...response.data });
    if (response && shouldUpdateStep) {
      if (formName === wirelessLandingPageForm && step === 1 && !orderData) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${wirelessOrderForm}`,
          search: searchString(isChannelSevenX ? 2 : 1, response.data.id)
        });
        window.location.reload();
      } else if (step >= steps.length) {
        /*
         * Logic for redirecting to Wireless home page after user hits Submit
         * button on Summary page
         */
        history.push({
          pathname: `/${locale}/${categoryName}/${wirelessLandingPageForm}`
        });
        window.location.reload();
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${wirelessOrderForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const onCybersecurityFormSubmit = async (
    formData,
    shouldUpdateStep = true,
    isFormLastStep
  ) => {
    const newStep = step + 1;
    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    let response = {};
    // Send order data to API
    const tempOrderData = attachedFiles(orderData, formData);
    response = await csSubmitPayload(
      {
        ...tempOrderData,
        ...formData,
        locale,
        categoryName,
        formName,
        step: newStep
      },
      isFormLastStep
    );
    setOrderData({ ...orderData, ...response.data });
    if (response && shouldUpdateStep) {
      if (formName === cybersecurityLandingPage && step === 1 && !orderData) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${cybersecurityForm}`,
          search: searchString(1, response.data.id)
        });
      } else if (step >= steps.length) {
        /*
         * Logic for redirecting to cybersecurity home page after user hits Submit
         * button on Summary page
         */
        history.push({
          pathname: `/${locale}/${categoryName}/${cybersecurityLandingPage}`
        });
        window.location.reload(); // remove this and add API call instead
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${cybersecurityForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const onFMFormSubmit = async (
    formData,
    shouldUpdateStep = true,
    isFormLastStep
  ) => {
    const newStep = step + 1;
    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    let response = {};
    // Send order data to API
    const tempOrderData = attachedFiles(orderData, formData);
    response = await fmSubmitPayload(
      {
        ...tempOrderData,
        ...formData,
        locale,
        categoryName,
        formName,
        step: newStep
      },
      isFormLastStep
    );
    setOrderData({ ...orderData, ...response.data });
    if (response && shouldUpdateStep) {
      if (formName === fullyManagedLandingPage && step === 1 && !orderData) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${fullyManagedForm}`,
          search: searchString(1, response.data.id)
        });
      } else if (step >= steps.length) {
        /*
         * Logic for redirecting to Wireless home page after user hits Submit
         * button on Summary page
         */
        history.push({
          pathname: `/${locale}/${categoryName}/${fullyManagedLandingPage}`
        });
        window.location.reload(); // remove this and add API call instead
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${fullyManagedForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const onVulcanFormSubmit = async (
    formData,
    shouldUpdateStep = true,
    isFormLastStep
  ) => {
    let newStep;
    if (
      formData.addProductVulcan &&
      formData.addProductVulcan.includes(vulBusinessConnect) &&
      step === 1
    ) {
      newStep = step + 2;
    } else if (formData && formData.orderIdTSB && step === 1) {
      newStep = step + 2;
    } else {
      newStep = step + 1;
    }

    // const newStep = step + 1;
    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    let response = {};
    // "New Order" on the landing page always starts a fresh order: never carry
    // over a previous order's state (and its id) or mark anything submitted.
    const isNewOrder = formName === vulcanLandingPage;
    const previousOrderData = isNewOrder ? undefined : orderData;

    // Send order data to API
    const tempOrderData = isNewOrder ? {} : attachedFiles(orderData, formData);
    response = await vulcanSubmitPayload(
      {
        ...tempOrderData,
        ...(isNewOrder ? _.omit(formData, 'id') : formData),
        locale,
        categoryName,
        formName,
        step: newStep
      },
      isNewOrder ? false : isFormLastStep
    );
    // The engine remounts fresh from `orderData` on every step transition
    // (formStepVulcan.js's isLoading gate) with no protection against a gap
    // in what the server echoes back — see mergeOrderDataAfterSubmit for the
    // real-data bug this fixes (a MultiCheckbox selection reverting after
    // Continue then Back).
    setOrderData(
      mergeOrderDataAfterSubmit(previousOrderData, formData, response.data)
    );
    if (response && shouldUpdateStep) {
      if (isNewOrder) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${vulcanForm}`,
          search: `?orderId=${response.data.id}&step=1&isEditing=true`
        });
      } else if (step >= steps.length) {
        /*
         * Logic for redirecting to Wireless home page after user hits Submit
         * button on Summary page
         */
        history.push({
          pathname: `/${locale}/${categoryName}/${vulcanLandingPage}`
        });
        window.location.reload(); // remove this and add API call instead
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${vulcanForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const onFMFormSubmitSOW = async (
    formData,
    shouldUpdateStep = true,
    isFormLastStep
  ) => {
    let newStep;
    if (formData.appendicesIncluded) {
      if (formData.appendicesIncluded === 'No') {
        newStep = step + 2;
      } else {
        newStep = step + 1;
      }
    } else if (orderData && orderData.appendicesIncluded) {
      if (orderData.appendicesIncluded === 'No') {
        newStep = step + 2;
      } else {
        newStep = step + 1;
      }
    }

    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    let response = {};
    // Send order data to API
    const tempOrderData = attachedFiles(orderData, formData);
    response = await fmSOWSubmitPayload(
      {
        ...tempOrderData,
        ...formData,
        locale,
        categoryName,
        formName,
        step: newStep
      },
      isFormLastStep
    );
    setOrderData({ ...orderData, ...response.data });
    if (response && shouldUpdateStep) {
      if (formName === fullyManagedSOWLandingPage && step === 1 && !orderData) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${fullyManagedSOWForm}`,
          search: searchString(1, response.data.id)
        });
      } else if (step >= steps.length) {
        /*
         * Logic for redirecting to Wireless home page after user hits Submit
         * button on Summary page
         */
        history.push({
          pathname: `/${locale}/${categoryName}/${fullyManagedSOWLandingPage}`
        });
        window.location.reload(); // remove this and add API call instead
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${fullyManagedSOWForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const onOCDRFormSubmit = async (
    formData,
    shouldUpdateStep = true,
    isFormLastStep
  ) => {
    let newStep;
    if (formData.productSelectionOCDR) {
      if (
        !formData.productSelectionOCDR.includes(
          fieldMicrosoftCloudAndAdvisoryServices
        )
      ) {
        newStep = step + 2;
      } else {
        newStep = step + 1;
      }
    } else if (orderData && orderData.productSelectionOCDR) {
      if (
        !orderData.productSelectionOCDR.includes(
          fieldMicrosoftCloudAndAdvisoryServices
        )
      ) {
        newStep = step + 2;
      } else {
        newStep = step + 1;
      }
    }

    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    let response = {};
    // Send order data to API
    const tempOrderData = attachedFiles(orderData, formData);
    response = await ocdrSubmitPayload(
      {
        ...tempOrderData,
        ...formData,
        locale,
        categoryName,
        formName,
        step: newStep
      },
      isFormLastStep
    );
    setOrderData({ ...orderData, ...response.data });
    if (response && shouldUpdateStep) {
      if (formName === ocdrLandingPage && step === 1 && !orderData) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${ocdrForm}`,
          search: searchString(1, response.data.id)
        });
      } else if (step >= steps.length) {
        /*
         * Logic for redirecting to Wireless home page after user hits Submit
         * button on Summary page
         */
        history.push({
          pathname: `/${locale}/${categoryName}/${ocdrLandingPage}`
        });
        window.location.reload(); // remove this and add API call instead
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${ocdrForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
    }
  };

  const handleSubmitAnotherBrpRequest = () => {
    updateStep(1);
    setShowConfirmation(false);
    setOrderData(null);
    history.push(`/${locale}/${categoryBrp}/${brpPrequalification}`);
  };

  const onBrpFormSubmit = async formData => {
    setShowLoading(true);
    const newStep = formName === brpPrequalification ? 1 : step + 1;
    const isLastStep = step === steps.length;

    const searchString = (paramStep, id = '') => {
      return orderData && orderData.id
        ? `?orderId=${orderData.id}&step=${paramStep}&isEditing=true`
        : `?orderId=${id}&step=${paramStep}&isEditing=true`;
    };
    // Send order data to API
    const response = await brpSubmitPayload(
      {
        ...orderData,
        ...formData,
        locale,
        categoryName,
        formName,
        step: newStep
      },
      isLastStep
    );

    if (response) {
      if (formName === brpPrequalification && step === 1 && !orderData) {
        updateStep(1);
        history.push({
          pathname: `/${locale}/${categoryName}/${brpOrderForm}`,
          search: searchString(1, response.data.id)
        });
        // window.location.reload();
      } else if (formName === brpOrderForm && isLastStep) {
        setShowConfirmation(true);
      } else {
        updateStep(newStep);
        history.push({
          pathname: `/${locale}/${categoryName}/${brpOrderForm}`,
          search: searchString(newStep, response.data.id)
        });
      }
      setOrderData({
        ...orderData,
        ...response.data
      });
      setShowLoading(false);
    }
  };

  // Downlad PDF
  const viewPDF = () => {
    const { id } = orderData;
    saveAndExit(orderData);
    requestOrderPdf({
      orderId: id,
      route: `${apiHost}/orders/${id}/proposal-pdf?lang=${locale}`,
      popup: openPdfPlaceholderWindow()
    });
  };

  const onSaveAndExit = async (formData, shouldExit = true) => {
    if (!orderId && !orderData) {
      const payload = {
        ...formData,
        locale,
        categoryName,
        formName,
        step
      };
      let response;
      switch (categoryName) {
        case categoryWireless:
          response = await wlsSubmitPayload(payload, false, orderPermissions);
          break;
        case categoryFullyManaged:
          response = await fmSubmitPayload(payload, false);
          break;
        case categoryVulcan:
          response = await vulcanSubmitPayload(payload, false);
          break;
        case categoryFullyManagedSOW:
          response = await fmSOWSubmitPayload(payload, false);
          break;
        case categoryCybersecurity:
          response = await csSubmitPayload(payload, false);
          break;
        case categoryOCDR:
          response = await ocdrSubmitPayload(payload, false);
          break;
        default:
          return;
      }
      if (response) {
        history.push({ pathname: getToolPath() });
      }
      return;
    }

    const putPayload = {
      ...orderData,
      ...formData,
      locale,
      categoryName,
      formName,
      step
    };
    saveAndExit(putPayload, shouldExit);
  };

  const onClick = e => {
    e.preventDefault();
    if (formRef.current.requestSubmit) {
      formRef.current.requestSubmit();
    } else {
      updateStep(1);
    }
  };

  if (!stepData) return null;

  const stepsCopy = steps;
  // rename 4th step to Device Order Details in case of devices only
  if (
    orderData &&
    orderData.orderNotEligibleforMssReason &&
    (orderData.orderNotEligibleforMssReason.includes(DevicesOnlyEnglish) ||
      orderData.orderNotEligibleforMssReason.includes(DevicesOnlyFrench))
  ) {
    const stepName = WirelessOrderDetails;
    const result = steps.find(item => item.fields.name === stepName);
    if (result && result.fields && result.fields.name) {
      result.fields.name = DeviceOrderDetails;
      steps[3] = result;
      steps.splice(4, 1);
    }
  } else {
    const stepName = DeviceOrderDetails;
    const result = steps.find(item => item.fields.name === stepName);
    if (result && result.fields && result.fields.name) {
      result.fields.name = WirelessOrderDetails;
      steps[3] = result;
      steps.splice(2, 0, stepsCopy[4]);
    }
  }

  // rename 4th step to Accessory Order Details in case of devices only
  if (
    orderData &&
    orderData.orderNotEligibleforMssReason &&
    (orderData.orderNotEligibleforMssReason.includes(AccessoriesOnlyEnglish) ||
      orderData.orderNotEligibleforMssReason.includes(AccessoriesOnlyFrench))
  ) {
    const stepName = WirelessOrderDetails;
    const result = steps.find(item => item.fields.name === stepName);
    if (result && result.fields && result.fields.name) {
      result.fields.name = AccessoryOrderDetails;
      steps[3] = result;
      steps.splice(4, 1);
    }
  } else {
    const stepName = AccessoryOrderDetails;
    const result = steps.find(item => item.fields.name === stepName);
    if (result && result.fields && result.fields.name) {
      result.fields.name = WirelessOrderDetails;
      steps[3] = result;
      steps.splice(2, 0, stepsCopy[4]);
    }
  }

  const getFormStep = () => {
    if (categoryName === categoryBrp)
      return (
        <Spinner
          label={<span>Saving Data...</span>}
          show={showLoading}
          fullScreen
        >
          <FormStepBRP
            stepData={stepData}
            steps={steps}
            progressBarSteps={progressBarSteps}
            step={step}
            updateStep={updateStep}
            history={history}
            orderData={stableOrderData}
            onSaveAndExit={onSaveAndExit}
            onViewPdf={viewPDF}
            onFormSubmit={onBrpFormSubmit}
            setFormRef={setFormRef}
            hideProgressBar={hideProgressBar}
            setOrderData={setOrderData}
            isEditing={isEditing}
            formName={formName}
          />
          {showConfirmation && (
            <BrpConfirmationBox
              handleSubmitAnotherBrpRequest={handleSubmitAnotherBrpRequest}
            />
          )}
        </Spinner>
      );
    if (categoryName === categoryWireless)
      return (
        <FormStepWLS
          stepData={stepData}
          steps={steps}
          progressBarSteps={progressBarSteps}
          step={step}
          updateStep={updateStep}
          history={history}
          orderData={stableOrderData}
          onSaveAndExit={onSaveAndExit}
          onViewPdf={viewPDF}
          onFormSubmit={onWlsFormSubmit}
          setFormRef={setFormRef}
          hideProgressBar={hideProgressBar}
          setOrderData={setOrderData}
          isEditing={isEditing}
          formName={formName}
        />
      );
    if (categoryName === categoryFullyManaged)
      return (
        <FormStepFM
          stepData={stepData}
          steps={steps}
          progressBarSteps={progressBarSteps}
          step={step}
          updateStep={updateStep}
          history={history}
          orderData={stableOrderData}
          onSaveAndExit={onSaveAndExit}
          onViewPdf={viewPDF}
          onFormSubmit={onFMFormSubmit}
          setFormRef={setFormRef}
          hideProgressBar={hideProgressBar}
          setOrderData={setOrderData}
          isEditing={isEditing}
          formName={formName}
        />
      );

    if (categoryName === categoryVulcan)
      return (
        <FormStepVulcan
          stepData={stepData}
          steps={steps}
          progressBarSteps={progressBarSteps}
          step={step}
          updateStep={updateStep}
          history={history}
          orderData={stableOrderData}
          onSaveAndExit={onSaveAndExit}
          onViewPdf={viewPDF}
          onFormSubmit={onVulcanFormSubmit}
          setFormRef={setFormRef}
          hideProgressBar={hideProgressBar}
          setOrderData={setOrderData}
          isEditing={isEditing}
          formName={formName}
          isCreateOrderButtonVisible={setIsCreateOrderButtonVisible}
          stepDataForSummary={dataSummary.fields.steps[step - 1]}
        />
      );
    if (categoryName === categoryFullyManagedSOW)
      return (
        <FormStepFMSOW
          stepData={stepData}
          steps={steps}
          progressBarSteps={progressBarSteps}
          step={step}
          updateStep={updateStep}
          history={history}
          orderData={stableOrderData}
          onSaveAndExit={onSaveAndExit}
          onFormSubmit={onFMFormSubmitSOW}
          setFormRef={setFormRef}
          hideProgressBar={hideProgressBar}
          setOrderData={setOrderData}
          isEditing={isEditing}
          formName={formName}
        />
      );
    if (categoryName === categoryOCDR)
      return (
        <FormStepOCDR
          stepData={stepData}
          steps={steps}
          progressBarSteps={progressBarSteps}
          step={step}
          updateStep={updateStep}
          history={history}
          orderData={stableOrderData}
          onSaveAndExit={onSaveAndExit}
          onViewPdf={viewPDF}
          onFormSubmit={onOCDRFormSubmit}
          setFormRef={setFormRef}
          hideProgressBar={hideProgressBar}
          setOrderData={setOrderData}
          isEditing={isEditing}
          formName={formName}
          isCreateOrderButtonVisible={setIsCreateOrderButtonVisible}
        />
      );
    if (categoryName === categoryCybersecurity)
      return (
        <FormStepCS
          stepData={stepData}
          steps={steps}
          progressBarSteps={progressBarSteps}
          step={step}
          updateStep={updateStep}
          history={history}
          orderData={stableOrderData}
          onSaveAndExit={onSaveAndExit}
          onViewPdf={viewPDF}
          onFormSubmit={onCybersecurityFormSubmit}
          setFormRef={setFormRef}
          hideProgressBar={hideProgressBar}
          setOrderData={setOrderData}
          isEditing={isEditing}
          formName={formName}
        />
      );
    return <Error text="This product is no longer available." />;
  };

  const handleActiveTabVUL = id => {
    setActiveTab(id);
    if (id === 'MyOrders') {
      const myOrderPage = localStorage.getItem('myOrderVULPage')
        ? localStorage.getItem('myOrderVULPage')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${vulcanLandingPage}`,
        search: `?page=${myOrderPage}`
      });
    } else if (id === 'OrderFulfillmentDashboard') {
      const OrderFulfillmentVULPage = localStorage.getItem(
        'OrderFulfillmentVLUPage'
      )
        ? localStorage.getItem('OrderFulfillmentVULPage')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${vulcanLandingPage}`,
        search: `?page=${OrderFulfillmentVULPage}`
      });
    }
  };

  const handleHistoryPush = pageNumber => {
    history.push({
      pathname: `/${locale}/${categoryName}/${wirelessLandingPageForm}`,
      search: `?page=${pageNumber}`
    });
  };

  const handleActiveWLSTab = id => {
    setActiveTab(id);
    if (id === 'MyOrders') {
      const MyOrdersPageWLS = localStorage.getItem('MyOrdersPageWLS')
        ? localStorage.getItem('MyOrdersPageWLS')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${wirelessLandingPageForm}`,
        search: `?page=${MyOrdersPageWLS}`
      });
    } else if (id === 'AllOrders') {
      const AllOrdersPageWLS = localStorage.getItem('AllOrdersPageWLS')
        ? localStorage.getItem('AllOrdersPageWLS')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${wirelessLandingPageForm}`,
        search: `?page=${AllOrdersPageWLS}`
      });
    } else if (id === 'ManageDevices') {
      const ManageDevices = localStorage.getItem('ManageDevices')
        ? localStorage.getItem('ManageDevices')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${wirelessLandingPageForm}`,
        search: `?page=${ManageDevices}`
      });
    } else if (id === 'ChannelOrders') {
      const ChannelOrdersPageWLS = localStorage.getItem('ChannelOrdersPageWLS')
        ? localStorage.getItem('ChannelOrdersPageWLS')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${wirelessLandingPageForm}`,
        search: `?page=${ChannelOrdersPageWLS}`
      });
    }
  };

  const handleActiveFMTab = id => {
    setActiveTab(id);
    if (id === 'MyOrders') {
      const myOrderPage = localStorage.getItem('myOrderPageNo')
        ? localStorage.getItem('myOrderPageNo')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${fullyManagedLandingPage}`,
        search: `?page=${myOrderPage}`
      });
    } else if (id === 'OrderFulfillmentDashboard') {
      const OrderFulfillmentPage = localStorage.getItem('OrderFulfillmentPage')
        ? localStorage.getItem('OrderFulfillmentPage')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${fullyManagedLandingPage}`,
        search: `?page=${OrderFulfillmentPage}`
      });
    } else if (id === 'DiscountApprovalDashboard') {
      const DiscountApprovalPage = localStorage.getItem('discountOrderPageNo')
        ? localStorage.getItem('discountOrderPageNo')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${fullyManagedLandingPage}`,
        search: `?page=${DiscountApprovalPage}`
      });
    }
  };

  const handleActiveCSTab = id => {
    setActiveTab(id);
    if (id === 'MyOrders') {
      const myOrderPage = localStorage.getItem('myOrderPageNo')
        ? localStorage.getItem('myOrderPageNo')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${cybersecurityLandingPage}`,
        search: `?page=${myOrderPage}`
      });
    } else if (id === 'OrderFulfillmentDashboard') {
      const OrderFulfillmentPage = localStorage.getItem('OrderFulfillmentPage')
        ? localStorage.getItem('OrderFulfillmentPage')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${cybersecurityLandingPage}`,
        search: `?page=${OrderFulfillmentPage}`
      });
    }
  };

  const handleActiveOCDRTab = id => {
    setActiveTab(id);
    if (id === 'MyOrders') {
      const myOrderPage = localStorage.getItem('myOrderPageNo')
        ? localStorage.getItem('myOrderPageNo')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${ocdrLandingPage}`,
        search: `?page=${myOrderPage}`
      });
    } else if (id === 'OrderFulfillmentDashboard') {
      const OrderFulfillmentPage = localStorage.getItem('OrderFulfillmentPage')
        ? localStorage.getItem('OrderFulfillmentPage')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${ocdrLandingPage}`,
        search: `?page=${OrderFulfillmentPage}`
      });
    }
  };

  const handleActiveFMSOWTab = id => {
    setActiveTab(id);
    if (id === 'MyOrders') {
      const myOrderPage = localStorage.getItem('myOrderPageNo')
        ? localStorage.getItem('myOrderPageNo')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${fullyManagedSOWLandingPage}`,
        search: `?page=${myOrderPage}`
      });
    } else if (id === 'OrderFulfillmentDashboard') {
      const OrderFulfillmentPage = localStorage.getItem('OrderFulfillmentPage')
        ? localStorage.getItem('OrderFulfillmentPage')
        : 1;
      history.push({
        pathname: `/${locale}/${categoryName}/${fullyManagedSOWLandingPage}`,
        search: `?page=${OrderFulfillmentPage}`
      });
    }
  };

  const getAdminTabs = currentlyActiveTab => {
    if (currentlyActiveTab === 'MyOrders')
      return (
        <SalesRepDashboard
          id="MyOrders"
          handleHistoryPush={handleHistoryPush}
          history={history}
          userId={userProfile.id}
        />
      );
    if (currentlyActiveTab === 'AllOrders')
      return (
        <AllOrdersDashboardWLS
          id="AllOrders"
          handleHistoryPush={handleHistoryPush}
          history={history}
        />
      );
    return <HardwareGridUpload />;
  };

  const showWLSTabs = () => {
    if (roleName === 'ADMIN')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveWLSTab}
              items={[
                { label: MyOrders, id: 'MyOrders' },
                { label: AllOrders, id: 'AllOrders' },
                { label: ManageDevicesCreditsFee, id: 'ManageDevices' }
              ]}
            />
          </TabBarWrapper>
          {getAdminTabs(activeTab)}
        </>
      );

    if (roleName === 'SALES_REP')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveWLSTab}
              items={[{ label: MyOrders, id: 'MyOrders' }]}
            />
          </TabBarWrapper>
          <SalesRepDashboard
            handleHistoryPush={handleHistoryPush}
            history={history}
            userId={userProfile.id}
          />
        </>
      );

    if (roleName === 'SALES_SUPPORT')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveWLSTab}
              items={[{ label: ChannelOrders, id: 'ChannelOrders' }]}
            />
          </TabBarWrapper>
          <ChannelOrdersDashboardWLS handleHistoryPush={handleHistoryPush} />
        </>
      );

    if (roleName === 'SALES_MANAGER')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveWLSTab}
              items={[
                { label: MyOrders, id: 'MyOrders' },
                { label: ChannelOrders, id: 'ChannelOrders' }
              ]}
            />
          </TabBarWrapper>
          {activeTab === 'MyOrders' ? (
            <SalesRepDashboard
              handleHistoryPush={handleHistoryPush}
              history={history}
              roleName={roleName}
              userId={userProfile.id}
            />
          ) : (
            <ChannelOrdersDashboardWLS handleHistoryPush={handleHistoryPush} />
          )}
        </>
      );
    return null;
  };

  const showVulcanTabs = () => {
    if (roleName === 'ADMIN' || roleName === 'SALES_MANAGER')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveTabVUL}
              items={[
                { label: myOrderHeader, id: 'MyOrders' },
                {
                  label: orderFulfilmentHeader,
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          {activeTab === 'MyOrders' ? (
            <MyOrdersDashboardVUL
              location={history.location}
              userId={userProfile.id}
            />
          ) : (
            <OrderFulfillmentDashboardVUL location={history.location} />
          )}
        </>
      );
    if (roleName === 'SALES_REP')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveTabVUL}
              items={[{ label: myOrderHeader, id: 'MyOrders' }]}
            />
          </TabBarWrapper>
          <MyOrdersDashboardVUL location={history.location} />
        </>
      );
    if (roleName === 'ORDER_FULFILLMENT' || roleName === 'SALES_SUPPORT')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveTabVUL}
              items={[
                {
                  label: orderFulfilmentHeader,
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <OrderFulfillmentDashboardVUL location={history.location} />
        </>
      );
    return null;
  };

  const showOCDRTabs = () => {
    if (roleName === 'ADMIN')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveOCDRTab}
              items={[
                { label: 'My Orders', id: 'MyOrders' },
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          {activeTab === 'MyOrders' ? (
            <MyOrdersDashboardOCDR
              location={history.location}
              userId={userProfile.id}
            />
          ) : (
            <OrderFulfillmentDashboardOCDR location={history.location} />
          )}
        </>
      );
    if (roleName === 'SALES_REP')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveOCDRTab}
              items={[{ label: 'My Orders', id: 'MyOrders' }]}
            />
          </TabBarWrapper>
          <MyOrdersDashboardOCDR
            location={history.location}
            userId={userProfile.id}
          />
        </>
      );
    if (roleName === 'SALES_MANAGER' || roleName === 'SALES_SUPPORT')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveOCDRTab}
              items={[
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <OrderFulfillmentDashboardOCDR location={history.location} />
        </>
      );
    return null;
  };

  const showFMTabs = () => {
    if (roleName === 'ADMIN')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMTab}
              items={[
                { label: 'My Orders', id: 'MyOrders' },
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                },
                {
                  label: 'Discount Approval Dashboard',
                  id: 'DiscountApprovalDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          {(() => {
            if (activeTab === 'MyOrders') {
              return (
                <MyOrdersDashboardFM
                  location={history.location}
                  userId={userProfile.id}
                />
              );
            }
            if (activeTab === 'OrderFulfillmentDashboard') {
              return (
                <OrderFulfillmentDashboardFM location={history.location} />
              );
            }
            return (
              <DiscountApprovalDashboardFM
                location={history.location}
                selectedDiscount={selectedDiscount}
              />
            );
          })()}
        </>
      );
    if (roleName === 'SALES_REP')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMTab}
              items={[{ label: 'My Orders', id: 'MyOrders' }]}
            />
          </TabBarWrapper>
          <MyOrdersDashboardFM
            location={history.location}
            userId={userProfile.id}
          />
        </>
      );
    if (roleName === 'SALES_MANAGER' || roleName === 'SALES_SUPPORT')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMTab}
              items={[
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <OrderFulfillmentDashboardFM location={history.location} />
        </>
      );
    if (roleName === 'PROPOSAL_HOUSE')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMTab}
              items={[
                {
                  label: 'Discount Approval Dashboard',
                  id: 'DiscountApprovalDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <DiscountApprovalDashboardFM location={history.location} />
        </>
      );

    return null;
  };

  const showCSTabs = () => {
    if (roleName === 'ADMIN')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveCSTab}
              items={[
                { label: 'My Orders', id: 'MyOrders' },
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          {activeTab === 'MyOrders' ? (
            <MyOrdersDashboardCS
              location={history.location}
              userId={userProfile.id}
            />
          ) : (
            <OrderFulfillmentDashboardCS location={history.location} />
          )}
        </>
      );
    if (roleName === 'SALES_REP')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveCSTab}
              items={[{ label: 'My Orders', id: 'MyOrders' }]}
            />
          </TabBarWrapper>
          <MyOrdersDashboardCS
            location={history.location}
            userId={userProfile.id}
          />
        </>
      );
    if (roleName === 'SALES_MANAGER')
      return (
        <>
          <Tabs copy="en" open={activeTab} onOpen={handleActiveCSTab}>
            <Tabs.Panel id="MyOrders" heading="My Orders" />
            <Tabs.Panel
              id="OrderFulfillmentDashboard"
              heading="Order Fulfillment Dashboard"
            />
          </Tabs>
          {activeTab === 'MyOrders' ? (
            <MyOrdersDashboardCS
              location={history.location}
              userId={userProfile.id}
            />
          ) : (
            <OrderFulfillmentDashboardCS location={history.location} />
          )}
        </>
      );
    if (roleName === 'SALES_SUPPORT')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveCSTab}
              items={[
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <OrderFulfillmentDashboardCS location={history.location} />
        </>
      );
    return null;
  };

  const showFMSOWTabs = () => {
    if (emailForSOW.includes(email)) {
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMSOWTab}
              items={[
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <OrderFulfillmentDashboardFMSOW
            location={history.location}
            userId={userProfile.id}
          />
        </>
      );
    }
    if (roleName === 'ADMIN')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMSOWTab}
              items={[
                { label: 'My Orders', id: 'MyOrders' },
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          {activeTab === 'MyOrders' ? (
            <MyOrdersDashboardFMSOW
              location={history.location}
              userId={userProfile.id}
            />
          ) : (
            <OrderFulfillmentDashboardFMSOW location={history.location} />
          )}
        </>
      );
    if (roleName === 'SALES_REP')
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMSOWTab}
              items={[{ label: 'My Orders', id: 'MyOrders' }]}
            />
          </TabBarWrapper>
          <MyOrdersDashboardFMSOW
            location={history.location}
            userId={userProfile.id}
          />
        </>
      );
    if (
      roleName === 'SALES_MANAGER' ||
      roleName === 'SALES_SUPPORT' ||
      roleName === 'ORDER_FULFILLMENT'
    )
      return (
        <>
          <TabBarWrapper>
            <Tabs
              itemTokens={{ maxWidth: 'none' }}
              value={activeTab}
              onChange={handleActiveFMSOWTab}
              items={[
                {
                  label: 'Order Fulfillment Dashboard',
                  id: 'OrderFulfillmentDashboard'
                }
              ]}
            />
          </TabBarWrapper>
          <OrderFulfillmentDashboardFMSOW
            location={history.location}
            userId={userProfile.id}
          />
        </>
      );
    return null;
  };

  const getCategoryLabel = catgName => {
    if (catgName === categoryWireless) return TELUSWirelessSalesOrderForm;
    return '';
  };

  if (step > 0 && !hideProgressBar) return <>{getFormStep()}</>;

  const categoryFormMap = {
    [categoryWireless]: showWLSTabs,
    [categoryFullyManaged]: showFMTabs,
    [categoryVulcan]: showVulcanTabs,
    [categoryFullyManagedSOW]: showFMSOWTabs,
    [categoryCybersecurity]: showCSTabs,
    [categoryOCDR]: showOCDRTabs
  };

  return (
    <>
      <div>
        <div
          style={{
            marginLeft: '6%',
            marginTop: '2%'
          }}
        >
          <Typography heading="h2" variant={{ size: 'h2' }} block>
            {getCategoryLabel(categoryName)}
          </Typography>
        </div>
      </div>
      <div>
        <div className="container 85%">
          <div id="title">
            <div id="title-wrapper">
              <div>
                <Typography heading="h2" variant={{ size: 'h2' }} block>
                  <RichText data={description} />
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container 85%">
        <div id="introduction">
          <div id="introduction-wrapper">
            <FormWrapper>
              <div className={image && 'column'}>
                {hideProgressBar && <>{getFormStep()}</>}
              </div>
              {image && (
                <div className="column image">
                  <Image image={image} />
                </div>
              )}
              {step === 1 &&
                showOrdersList &&
                roleName !== 'SALES_SUPPORT' &&
                showQuoteButton() && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <Button
                      type="button"
                      onClick={e => onClick(e)}
                      variant={{ priority: 'high' }}
                      style={{ marginBottom: '1em' }}
                      disabled={isCreateOrderButtonVisible}
                    >
                      {[
                        categoryWireless,
                        categoryFullyManaged,
                        categoryVulcan,
                        categoryFullyManagedSOW,
                        categoryCybersecurity,
                        categoryOCDR
                      ].includes(categoryName)
                        ? newOrderButtonLabel
                        : newQuoteButtonLabel}
                    </Button>
                  </div>
                )}
            </FormWrapper>
          </div>
        </div>
      </div>
      {steps && steps.length > 1 && !skipIntro && (
        <div className="container 85%" style={{ paddingBottom: '3rem' }}>
          <span>
            <Button
              onClick={e => onClick(e)}
              variant={{ priority: 'high' }}
              style={{ alignSelf: 'flex-start' }}
            >
              {categoryName === categoryWireless ||
              categoryName === categoryFullyManaged ||
              categoryName === categoryVulcan ||
              categoryName === categoryFullyManagedSOW ||
              categoryName === categoryCybersecurity ||
              categoryName === categoryOCDR
                ? newOrderButtonLabel
                : newQuoteButtonLabel}
            </Button>
          </span>
        </div>
      )}
      {step === 1 && showOrdersList && categoryFormMap[categoryName]?.()}
    </>
  );
};

Form.propTypes = {
  data: PropTypes.shape({
    fields: PropTypes.objectOf(PropTypes.object)
  }).isRequired,
  orderId: PropTypes.string,
  initialStep: PropTypes.string,
  isEditing: PropTypes.string,
  dataSummary: PropTypes.shape({
    fields: PropTypes.shape({
      steps: PropTypes.arrayOf(PropTypes.object)
    })
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string
    })
  }).isRequired
};

export default Form;
